import { debounce, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { fetchOptionSearch } from '../../../services/searchService'
import {
  OnboardSearchAutocompleteViewContainer,
  OnboardSearchAutocompleteViewInnerContainer,
  OnboardSearchContainer,
  OnboardSearchInputView,
  OnboardSearchInputViewIconSearch,
  OnboardSearchInputViewInputAdornment,
  OnboardSearchInputViewTextField,
  OnboardSearchResultSearchTermContainer,
  OnboardSearchResultSearchTermIconCornerDownLeft,
  OnboardSearchResultSearchTermTypographyNoItem,
  OnboardSearchResultSearchTermTypographyResult,
} from './styled/OnboardSearch.styled'

// Phase 2 (search overhaul): the onboarding/filter option typeahead no longer queries Elastic
// directly from the browser (no more REACT_APP_ELASTIC_API_KEY / ElasticsearchAPIConnector). It now
// calls the resactly-service `/search/options` proxy (REACT_APP_SERVICE_URL) which forwards to
// resactly-dis. The `indexKey` prop (a REACT_APP_*_INDEX env var NAME) is mapped to a stable
// category token so a raw Elastic index name never leaves the client.
const CATEGORY_BY_INDEX_KEY: Record<string, string> = {
  REACT_APP_RESTAURANT_INDEX: 'restaurants',
  REACT_APP_INGREDIENTS_INDEX: 'ingredients',
  REACT_APP_CUISINES_INDEX: 'cuisines',
  REACT_APP_FOOD_PREPARATION_INDEX: 'food-preparation',
  REACT_APP_AMBIENCE_INDEX: 'ambience',
  REACT_APP_DIETARY_PREFERENCES_INDEX: 'dietary-preferences',
  REACT_APP_ALLERGIES_INDEX: 'allergies',
  REACT_APP_FAVORITE_REVIEWERS_INDEX: 'favorite-reviewers',
}

interface Props {
  indexKey: string
  setWord: (word: string) => void
  // The searchable field within the resolved index. Backend maps `category` -> index and uses this
  // field (whitelisted to name/value server-side), so behavior matches the old per-call-site config.
  searchFieldName?: string
  searchWidth?: string | undefined
}

const OnboardSearchBox: React.FC<Props> = ({
  indexKey,
  setWord,
  searchFieldName = 'value',
  searchWidth = undefined,
}: Props): JSX.Element => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const inputRef = useRef<HTMLInputElement>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<string[]>([])
  const requestSeqRef = useRef(0)

  const category = CATEGORY_BY_INDEX_KEY[indexKey] ?? ''

  const fetchResults = useMemo(
    () =>
      debounce((term: string, onResults: (options: string[]) => void) => {
        void (async () => {
          try {
            const options = await fetchOptionSearch({
              category,
              field: searchFieldName,
              q: term,
            })
            onResults(options)
          } catch {
            onResults([])
          }
        })()
      }, 1000),
    [category, searchFieldName],
  )

  useEffect(() => {
    if (!searchTerm) {
      setResults([])
      return
    }
    const reqId = ++requestSeqRef.current
    fetchResults(searchTerm, (options) => {
      if (reqId !== requestSeqRef.current) return
      setResults(options)
    })
  }, [searchTerm, fetchResults])

  const addExistingValue = (value: string): void => {
    setWord(value)
  }

  const onAddValue = (val: string): void => {
    setWord(val)
    setSearchTerm('')
    setResults([])
  }

  return (
    <OnboardSearchContainer customWidth={searchWidth}>
      <OnboardSearchInputView searchWidth={searchWidth}>
        <OnboardSearchInputViewTextField
          autoFocus
          inputRef={inputRef}
          value={searchTerm}
          placeholder="Search or add new"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <OnboardSearchInputViewInputAdornment>
                <OnboardSearchInputViewIconSearch />
              </OnboardSearchInputViewInputAdornment>
            ),
            sx: {
              padding: '0 10px',
              fontSize: '16px',
              fontWeight: 500,
            },
            style: { borderRadius: '10px' },
          }}
        />

        {results.length > 0 && (
          <OnboardSearchAutocompleteViewContainer>
            {results.map((result, i) => (
              <OnboardSearchAutocompleteViewInnerContainer
                key={`${result}-${i}`}
                id="MuiInputBase-input-MuiOutlinedInput-input-check"
                onClick={() => addExistingValue(result)}
              >
                {result}
              </OnboardSearchAutocompleteViewInnerContainer>
            ))}
          </OnboardSearchAutocompleteViewContainer>
        )}
      </OnboardSearchInputView>

      {searchTerm && searchTerm.length > 0 && results.length === 0 && (
        <OnboardSearchResultSearchTermContainer
          customWidth={isSmallScreen ? '90vw' : searchWidth}
          onClick={() => onAddValue(searchTerm)}
        >
          <OnboardSearchResultSearchTermIconCornerDownLeft />
          <OnboardSearchResultSearchTermTypographyNoItem>
            No Item Found. Click Enter to Add &nbsp;
          </OnboardSearchResultSearchTermTypographyNoItem>
          <OnboardSearchResultSearchTermTypographyResult>
            {searchTerm}
          </OnboardSearchResultSearchTermTypographyResult>
        </OnboardSearchResultSearchTermContainer>
      )}
    </OnboardSearchContainer>
  )
}

export default React.memo(OnboardSearchBox)
